import { useNav } from "./router";
import { ROUTE_READ_PERM } from "./routeTable";
import { SCREENS, MIGRATED_ROUTES } from "./screens";
import { can } from "./access";
import { useViewport } from "./useResponsive";
import AppLauncher from "./AppLauncher";
import { TagLabel } from "@v0y4ge/jrni-ui";
import { EventOverlay } from "../screens/events/detail/EventOverlay";
import { ErrorBoundary } from "./ErrorBoundary";
import { Screen as ScreenChrome } from "./chrome";
import { useT } from "../lib/i18n";

/**
 * AppRouter — renders the active route's screen, enforcing the read-level
 * capability guard (deep-link protection). Screens own their own chrome via the
 * ported <Screen> layout; this just resolves route → component.
 */
export function AppRouter() {
  const nav = useNav();
  const vp = useViewport();
  // Phone & tablet home is the app launcher; the full Studio is desktop-only.
  if (nav.route === "dashboard" && vp !== "desktop") return <AppLauncher />;
  const migrated = MIGRATED_ROUTES.has(nav.route);
  const perm = ROUTE_READ_PERM[nav.route];
  // Every screen and the event drawer render inside their own boundary, so a
  // render crash is contained to that surface instead of unmounting the shell
  // (2026-07-27). `resetKey` clears a caught error as soon as the route changes,
  // which makes navigating away the natural recovery.
  const drawer = nav.drawerEvent
    ? <ErrorBoundary scope={`drawer:${nav.drawerEvent}`} label="Event drawer" variant="pane" resetKey={String(nav.drawerEvent)}><EventOverlay ev={nav.drawerEvent} /></ErrorBoundary>
    : null;
  if (perm && !can(perm)) return <><NoAccess />{drawer}</>;
  const Screen = SCREENS[nav.route];
  if (!Screen) return <><UnmigratedBadge /><NotFound route={nav.route} />{drawer}</>;
  return (
    <>
      {!migrated ? <UnmigratedBadge /> : null}
      <ErrorBoundary scope={nav.route} label={nav.route} resetKey={nav.route}
        wrap={(fallback) => <ScreenChrome>{fallback}</ScreenChrome>}><Screen /></ErrorBoundary>
      {drawer}
    </>
  );
}

/**
 * Unmigrated badge — fixed top-right, very high z-index, non-interactive.
 * Shows on every route NOT in MIGRATED_ROUTES (not yet wired to the live data
 * layer). A screen graduates — and the badge disappears — the moment it's added
 * to REAL_SCREENS. Built on the design-system TagLabel (token-based, no hex).
 */
function UnmigratedBadge() {
  const t = useT();
  return (
    <div aria-label={t("shell.appRouter.thisScreenIsNotYetMigratedToTheLiveDataLayer", "This screen is not yet migrated to the live data layer")}
      style={{ position: "fixed", top: 12, right: 12, zIndex: 2000000, pointerEvents: "none", borderRadius: 999, boxShadow: "0 4px 14px rgba(20, 16, 46, 0.18)" }}>
      <TagLabel color="orange" type="dot">{t("shell.appRouter.unmigrated", "Unmigrated")}</TagLabel>
    </div>
  );
}

function Centered({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 40, fontFamily: "var(--jrni-font-family-sans)" }}>
      <div style={{ maxWidth: 420, textAlign: "center" }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: "var(--jrni-color-neutral-1)", marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 14, color: "var(--jrni-color-neutral-2)", lineHeight: 1.5 }}>{body}</div>
      </div>
    </div>
  );
}
function NoAccess() {
  const t = useT(); return <Centered title={t("shell.appRouter.youDonTHaveAccessToThis", "You don't have access to this")} body={t("shell.appRouter.yourRoleCanTOpenThisSurfaceAskAnAdminIfYou", "Your role can't open this surface. Ask an admin if you need access.")} />; }
function NotFound({ route }: { route: string }) {
  const t = useT(); return <Centered title={t("shell.appRouter.pageNotFound", "Page not found")} body={t("shell.appRouter.noScreenIsRegisteredForRoute.icu", { route })} />; }
